/* eslint-disable react/prop-types */
import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Link } from 'gatsby'
import device from '../lib/mediaQueries'

const randomPercentageInside = (maxValue = 50) => `${Math.random() * maxValue}%`
const randomScale = () => 1 + Math.random() - Math.random()

// Which edge of the card the cursor is nearest — used so the ball slides in
// from the side the cursor entered and slides out toward the side it leaves.
const edgeFromEvent = (event) => {
  const rect = event.currentTarget.getBoundingClientRect()
  const distances = {
    left: event.clientX - rect.left,
    right: rect.right - event.clientX,
    top: event.clientY - rect.top,
    bottom: rect.bottom - event.clientY,
  }
  return Object.keys(distances).reduce((closest, edge) =>
    distances[edge] < distances[closest] ? edge : closest,
  )
}

// A resting spot fully outside the given edge, at a random point along it.
const OFFSCREEN = 130
const positionOutside = (edge) => {
  switch (edge) {
    case 'left':
      return { top: randomPercentageInside(100), left: `-${OFFSCREEN}%` }
    case 'right':
      return { top: randomPercentageInside(100), left: `${OFFSCREEN}%` }
    case 'top':
      return { top: `-${OFFSCREEN}%`, left: randomPercentageInside(100) }
    case 'bottom':
    default:
      return { top: `${OFFSCREEN}%`, left: randomPercentageInside(100) }
  }
}

const Title = styled.p`
  font-size: 22px;
  font-weight: bold;
  color: ${(props) => props.scheme.main};
  margin: 0px;
  transition: color 0.2s ease-out;
  text-align: center;
`

// Faint featured photo behind each tile. Sits above the tile's own
// background-color (negative z-index) but below the title and difference
// circle. Always desaturated and multiply-blended so it reads as a subtle
// texture rather than a photo; on hover it zooms in and blurs.
const Backdrop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-size: cover;
  background-position: center;
  opacity: 0.3;
  pointer-events: none;
  z-index: -1;
  filter: grayscale(1);
  mix-blend-mode: multiply;
  transition: transform 0.2s ease-out, filter 0.2s ease-out;
`

const DifferenceObject = styled.div`
  position: absolute;
  width: 200px;
  height: 200px;
  background-color: ${(props) => props.scheme.inverted};
  top: 110%;
  left: 110%;
  border-radius: 200px;
  mix-blend-mode: difference;
  pointer-events: none;
  transition: all 0.2s ease-out;
  z-index: 12;
`

const Container = styled(Link)`
  width: 100%;
  min-height: 220px;

  ${device.tablet} {
    width: 33.33%;
    min-height: 300px;
  }
  /* ${device.laptop} {
    width: 25%;
  } */
  position: relative;
  box-sizing: border-box;
  padding: 20px;

  text-decoration: none;
  cursor: pointer;
  background-color: ${(props) => props.scheme.background};
  transition: background-color 0.2s ease-out;
  z-index: 11;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  &:hover,
  &:active {
    background-color: ${(props) => props.scheme.background};

    ${Title} {
      color: ${(props) => props.scheme.main};
    }

    ${Backdrop} {
      transform: scale(1.15);
      filter: grayscale(1) blur(6px);
    }
  }

  /* Desktop only: drop every tile to a shared, near-neutral checkerboard
     (two dark shades that differ only slightly, both a touch above the page
     background) and desaturate the moving difference circle. On hover the
     tile transitions back to its own scheme's colors. Touch layouts (no
     hover) keep their full color. */
  ${device.laptop} {
    filter: saturate(0);
    background-color: #14141b;
    transition: background-color 0.2s ease-out, filter 0.2s ease-out;

    &:nth-child(even) {
      background-color: #1b1b23;
    }

    ${Title} {
      color: #9a9aa3;
    }

    &:hover,
    &:active {
      filter: saturate(1);
      background-color: ${(props) => props.scheme.background};

      ${Title} {
        color: ${(props) => props.scheme.main};
      }
    }
  }
`

const ProjectAward = ({ scheme, title, path, image }) => {
  const [position, setPosition] = React.useState(() => ({
    ...positionOutside('right'),
    scale: 1,
    animate: true,
  }))
  const isHovering = React.useRef(false)
  const rafRef = React.useRef(null)

  const cancelScheduled = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = null
  }

  React.useEffect(() => cancelScheduled, [])

  return (
    <Container
      scheme={scheme}
      to={path}
      onMouseEnter={(event) => {
        isHovering.current = true
        cancelScheduled()
        const edge = edgeFromEvent(event)
        // Park the ball just off the edge the cursor entered from, with no
        // transition so the jump is invisible (it's outside the clipped card)…
        setPosition({ ...positionOutside(edge), scale: 1, animate: false })
        // …then, once that frame has painted, animate it inward from that side.
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = requestAnimationFrame(() => {
            if (!isHovering.current) return
            setPosition({
              top: randomPercentageInside(),
              left: randomPercentageInside(),
              scale: randomScale(),
              animate: true,
            })
          })
        })
      }}
      onMouseLeave={(event) => {
        isHovering.current = false
        cancelScheduled()
        const edge = edgeFromEvent(event)
        // Slide out toward whichever edge the cursor is leaving through.
        setPosition({ ...positionOutside(edge), scale: 1, animate: true })
      }}
    >
      {image && <Backdrop style={{ backgroundImage: `url(${image})` }} />}
      <Title scheme={scheme}>{title}</Title>
      <DifferenceObject
        scheme={scheme}
        style={{
          top: position.top,
          left: position.left,
          transform: `scale(${position.scale}, ${position.scale})`,
          ...(position.animate ? null : { transition: 'none' }),
        }}
      />
    </Container>
  )
}

ProjectAward.propTypes = {
  title: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  image: PropTypes.string,
}

export default ProjectAward
